import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  CircularProgress,
  Fade,
  Tabs,
  Tab,
} from '@mui/material';
import { Save as SaveIcon, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import UserAvatar from '../../../../components/Avatar/UserAvatar';
import NotebookAgentAvatar from '../../../../components/Avatar/NotebookAgentAvatar';
import MarkdownRenderer from '../../../../components/Common/MarkdownRenderer';
import ExerciseContent from '../../../../components/Exercise/ExerciseContent';

export default function ChatMessages({
  notebookTitle,
  agentAvatar,
  notebookId,
  messages,
  streamingContent,
  sending,
  loadingHistory,
  reviewLoading = false,
  selectingNextQuestion = false,
  reviewExerciseIndices = [],
  currentExerciseIndex = 0,
  setCurrentExerciseIndex,
  currentUser,
  isTeacher,
  generatedDescriptionForSave,
  savingDescription,
  messagesEndRef,
  handleSaveDescriptionToNotebook,
  handleExerciseAnswerSubmitted,
  lastExercise,
  lastExerciseAnswerInInput = false,
  codeEditorOpen = true,
  setCodeEditorOpen,
}) {
  const [codeTestTabByIdx, setCodeTestTabByIdx] = useState({});

  if (loadingHistory) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', zIndex: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', zIndex: 1 }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          p: 2,
          m: 2,
          borderRadius: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(6px) saturate(120%)',
          WebkitBackdropFilter: 'blur(6px) saturate(120%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.700', borderRadius: 4 },
        }}
      >
        {(reviewLoading || selectingNextQuestion) ? (
          <Fade in>
            <Box
              sx={{
                flex: 1,
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 6,
                px: 4,
                backgroundColor: 'transparent',
                background: 'none',
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={500}>
                正在选题…
              </Typography>
            </Box>
          </Fade>
        ) : messages.length === 0 && !streamingContent ? (
          <Fade in>
            <Box
              sx={{
                flex: 1,
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 6,
                px: 4,
                backgroundColor: 'transparent',
                background: 'none',
              }}
            >
              <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
                {notebookTitle || '笔记本'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                我会根据本笔记本的知识点（不含练习题）回答你的问题，支持 Markdown 与公式
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary">试试问：概念解释、定理证明、章节总结</Typography>
              </Stack>
            </Box>
          </Fade>
        ) : null}
        {!reviewLoading && !selectingNextQuestion && (
        <>
        <Stack spacing={2}>
          {reviewExerciseIndices.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Button
                size="small"
                startIcon={<NavigateBefore />}
                onClick={() => setCurrentExerciseIndex((i) => Math.max(0, i - 1))}
                disabled={currentExerciseIndex <= 0}
              >
                上一题
              </Button>
              <Typography variant="body2" color="text.secondary">
                {currentExerciseIndex + 1} / {reviewExerciseIndices.length}
              </Typography>
              <Button
                size="small"
                endIcon={<NavigateNext />}
                onClick={() => setCurrentExerciseIndex((i) => Math.min(reviewExerciseIndices.length - 1, i + 1))}
                disabled={currentExerciseIndex >= reviewExerciseIndices.length - 1}
              >
                下一题
              </Button>
            </Stack>
          )}
          {messages.map((msg, idx) => {
            if (reviewExerciseIndices.length > 0) {
              const currExIdx = reviewExerciseIndices[currentExerciseIndex];
              const nextExIdx = reviewExerciseIndices[currentExerciseIndex + 1] ?? messages.length;
              const startIdx = Math.max(0, currExIdx - 1);
              const endIdx = nextExIdx < messages.length ? nextExIdx - 1 : messages.length; // 不包含下一题的选题理由
              if (idx < startIdx || idx >= endIdx) return null;
            }
            if (msg.type === 'exercise' && msg.example) {
              const isCode = msg.example.questionType === 'code';
              return (
                <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                  <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={36} />
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: 900,
                      overflow: 'hidden',
                      p: 2,
                      borderRadius: '18px 18px 18px 4px',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {msg._previewLabel && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {msg._previewLabel}
                      </Typography>
                    )}
                    {isCode ? (
                      <>
                        <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#ffffff', '& .markdown-code-block': { bgcolor: '#1e1e1e', borderRadius: 2 } }}>
                          <MarkdownRenderer content={msg.example.question} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          请在页面右边编写代码并作答。
                        </Typography>
                        {(msg.example.publicTests || (msg.example.secretTests && isTeacher)) && (
                          <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            {msg.example.publicTests && msg.example.secretTests && isTeacher ? (
                              <>
                                <Tabs
                                  value={codeTestTabByIdx[idx] ?? 0}
                                  onChange={(e, v) => setCodeTestTabByIdx((prev) => ({ ...prev, [idx]: v }))}
                                  sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
                                >
                                  <Tab label="公开测试用例" />
                                  <Tab label="秘密测试用例" />
                                </Tabs>
                                <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: '0 0 8px 8px' }}>
                                  {(codeTestTabByIdx[idx] ?? 0) === 0 ? (
                                    <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ margin: 0, padding: 0, background: 'transparent', fontSize: '0.8rem' }} showLineNumbers={false}>
                                      {msg.example.publicTests.replace(/```python\n?/g, '').replace(/```/g, '').trim()}
                                    </SyntaxHighlighter>
                                  ) : (
                                    <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ margin: 0, padding: 0, background: 'transparent', fontSize: '0.8rem' }} showLineNumbers={false}>
                                      {msg.example.secretTests.replace(/```python\n?/g, '').replace(/```/g, '').trim()}
                                    </SyntaxHighlighter>
                                  )}
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.3)' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>公开测试用例</Typography>
                                <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ margin: 0, padding: 0, background: 'transparent', fontSize: '0.8rem' }} showLineNumbers={false}>
                                  {(msg.example.publicTests || '').replace(/```python\n?/g, '').replace(/```/g, '').trim()}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        )}
                        {setCodeEditorOpen && (
                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setCodeEditorOpen(true)}
                              disabled={codeEditorOpen}
                            >
                              打开代码编辑器
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setCodeEditorOpen(false)}
                              disabled={!codeEditorOpen}
                            >
                              关闭代码编辑器
                            </Button>
                          </Stack>
                        )}
                      </>
                    ) : (
                      <ExerciseContent
                        example={msg.example}
                        onAnswerSubmitted={handleExerciseAnswerSubmitted}
                        embedded
                        useChatPanelForAnswer={lastExerciseAnswerInInput && lastExercise?.example?.id === msg.example?.id}
                      />
                    )}
                  </Paper>
                </Stack>
              );
            }
            return (
              <Stack
                key={idx}
                direction="row"
                spacing={1.5}
                justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                alignItems="flex-start"
              >
                {msg.role === 'assistant' && <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={36} />}
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: msg.role === 'assistant' ? '90%' : '75%',
                    minWidth: msg.role === 'assistant' ? 280 : undefined,
                    p: 2,
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'transparent',
                    color: msg.role === 'user' ? 'primary.contrastText' : '#ffffff',
                    ...(msg.role === 'assistant' && {
                      '& .MuiTypography-root:not(.markdown-code-block *), & p:not(.markdown-code-block *), & span:not(.markdown-code-block *), & div:not(.markdown-code-block *), & .katex:not(.markdown-code-block *), & .katex *:not(.markdown-code-block *)': { color: '#ffffff !important' },
                      '& a:not(.markdown-code-block *)': { color: 'rgba(255,255,255,0.9) !important' },
                      '& .markdown-code-block': { backgroundColor: '#1e1e1e !important', borderRadius: 2 },
                    }),
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#ffffff !important' }}>
                      <MarkdownRenderer content={msg.content || ''} />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                  )}
                </Paper>
                {msg.role === 'user' && (
                  <UserAvatar user={currentUser} size={36} sx={{ width: 36, height: 36 }} />
                )}
              </Stack>
            );
          })}
          {streamingContent && (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={36} />
              <Paper
                elevation={0}
                sx={{
                  maxWidth: '90%',
                  minWidth: 280,
                  p: 2,
                  borderRadius: '18px 18px 18px 4px',
                  bgcolor: 'transparent',
                  color: '#ffffff',
                  '& .MuiTypography-root:not(.markdown-code-block *), & p:not(.markdown-code-block *), & span:not(.markdown-code-block *), & div:not(.markdown-code-block *), & .katex:not(.markdown-code-block *), & .katex *:not(.markdown-code-block *)': { color: '#ffffff !important' },
                  '& a:not(.markdown-code-block *)': { color: 'rgba(255,255,255,0.9) !important' },
                  '& .markdown-code-block': { backgroundColor: '#1e1e1e !important', borderRadius: 2 },
                }}
              >
                <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#ffffff !important' }}>
                  <MarkdownRenderer content={streamingContent} />
                </Box>
              </Paper>
            </Stack>
          )}
          {sending && !streamingContent && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={36} />
              <Paper elevation={0} sx={{ p: 2, borderRadius: '18px 18px 18px 4px', bgcolor: 'transparent', color: '#ffffff' }}>
                <CircularProgress size={20} thickness={4} />
              </Paper>
            </Stack>
          )}
          {isTeacher && generatedDescriptionForSave && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={savingDescription ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveDescriptionToNotebook}
                disabled={savingDescription}
                sx={{ borderRadius: 2 }}
              >
                {savingDescription ? '保存中...' : '保存到笔记本'}
              </Button>
            </Box>
          )}
        </Stack>
        <div ref={messagesEndRef} />
        </>
        )}
      </Box>
    </Box>
  );
}
